<template>
  <Dialog
    :visible="visible"
    :header="
      scannedData?.firstName || scannedData?.lastName
        ? `New Supplier - ${[scannedData?.firstName, scannedData?.middleName, scannedData?.lastName].filter(Boolean).join(' ')}`
        : 'New Supplier'
    "
    :modal="true"
    :closable="false"
    :style="{ width: '95vw', maxWidth: '1200px' }"
    :breakpoints="{ '960px': '95vw' }"
  >
    <div class="w-full" style="height: 80vh">
      <div class="grid" style="height: 100%">
        <!-- Left: ID Photo and Scanned Info -->
        <div class="col-12 md:col-6 flex flex-column gap-3 p-3">
          <h3 class="text-lg font-semibold">ID Document</h3>
          <div class="flex gap-3">
            <!-- Front Image -->
            <div class="flex flex-column flex-1">
              <label class="text-sm font-medium mb-2">Front ID</label>
              <div
                class="border-1 border-dashed surface-border border-round flex align-items-center justify-content-center cursor-pointer hover:surface-100 transition-colors id-photo-container-half"
                @click="triggerFrontSelect"
              >
                <template v-if="frontImageUrl">
                  <img :src="frontImageUrl" alt="Front ID" class="border-round id-photo-image" />
                </template>
                <template v-else-if="frontCapture.state.uploadedImagePreview">
                  <img
                    :src="frontCapture.state.uploadedImagePreview"
                    alt="Front Preview"
                    class="border-round id-photo-image"
                  />
                </template>
                <template v-else>
                  <div class="text-center text-gray-500">
                    <i class="pi pi-image text-3xl mb-2"></i>
                    <div class="text-sm">No ID Photo</div>
                    <span class="text-xs">Click to upload front</span>
                  </div>
                </template>
              </div>
              <input
                ref="frontInputRef"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onFrontFileSelect"
              />
            </div>
            <!-- Back Image -->
            <div class="flex flex-column flex-1">
              <label class="text-sm font-medium mb-2">Back ID (Optional)</label>
              <div
                class="border-1 border-dashed surface-border border-round flex align-items-center justify-content-center cursor-pointer hover:surface-100 transition-colors id-photo-container-half"
                @click="triggerBackSelect"
              >
                <template v-if="backImageUrl">
                  <img :src="backImageUrl" alt="Back ID" class="border-round id-photo-image" />
                </template>
                <template v-else-if="backCapture.state.uploadedImagePreview">
                  <img
                    :src="backCapture.state.uploadedImagePreview"
                    alt="Back Preview"
                    class="border-round id-photo-image"
                  />
                </template>
                <template v-else>
                  <div class="text-center text-gray-500">
                    <i class="pi pi-image text-3xl mb-2"></i>
                    <div class="text-sm">No Back Photo</div>
                    <span class="text-xs">Optional: click to upload back</span>
                  </div>
                </template>
              </div>
              <input
                ref="backInputRef"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onBackFileSelect"
              />
            </div>
          </div>

          <div class="mt-3">
            <h4 class="text-md font-semibold mb-2">Scanned Details</h4>
            <div class="text-sm">
              <div>
                <strong>Name:</strong> {{ scannedData?.firstName }} {{ scannedData?.middleName }}
                {{ scannedData?.lastName }}
              </div>
              <div><strong>ID #:</strong> {{ scannedData?.idNumber }}</div>
              <div><strong>Issuer:</strong> {{ scannedData?.state }}</div>
              <div v-if="scannedData?.dateOfBirth">
                <strong>DOB:</strong> {{ scannedData?.dateOfBirth }}
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Contact Info + Questionnaire -->
        <div class="col-12 md:col-6 flex flex-column gap-3 p-3">
          <h3 class="text-lg font-semibold">Contact Info</h3>
          <div class="grid">
            <div class="col-12 md:col-6">
              <span class="p-float-label w-full">
                <InputMask
                  id="phone"
                  v-model="contactForm.phone"
                  mask="(999) 999-9999"
                  class="w-full"
                />
                <label for="phone">Phone</label>
              </span>
            </div>
            <div class="col-12 md:col-6">
              <span class="p-float-label w-full">
                <InputText id="email" v-model="contactForm.email" class="w-full" />
                <label for="email">Email</label>
              </span>
            </div>
          </div>
          <div class="flex flex-column gap-3">
            <div class="field">
              <label class="block mb-2">How did you hear about our scrap metal yard?</label>
              <div class="flex flex-column gap-2">
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="referral"
                    value="referral"
                    name="referralSource"
                  />
                  <span class="ml-2">Referral (Friend/Colleague)</span>
                </label>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="online-search"
                    value="online-search"
                    name="referralSource"
                  />
                  <span class="ml-2">Online Search (maps or search)</span>
                </label>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="social-media"
                    value="social-media"
                    name="referralSource"
                  />
                  <span class="ml-2">Social Media</span>
                </label>
                <div v-if="questionnaire.referralSource === 'social-media'" class="ml-4">
                  <InputText
                    v-model="questionnaire.socialMediaDetails"
                    placeholder="Please specify: Facebook, Instagram, etc."
                    class="w-full"
                  />
                </div>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="passing-by"
                    value="passing-by"
                    name="referralSource"
                  />
                  <span class="ml-2">Passing By/Signage</span>
                </label>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="industry-publication"
                    value="industry-publication"
                    name="referralSource"
                  />
                  <span class="ml-2">Industry Publication/Trade Show</span>
                </label>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="existing-customer"
                    value="existing-customer"
                    name="referralSource"
                  />
                  <span class="ml-2">Existing Customer (We've sold to you)</span>
                </label>
                <label class="flex align-items-center">
                  <RadioButton
                    v-model="questionnaire.referralSource"
                    inputId="other"
                    value="other"
                    name="referralSource"
                  />
                  <span class="ml-2">Other</span>
                </label>
                <div v-if="questionnaire.referralSource === 'other'" class="ml-4">
                  <InputText
                    v-model="questionnaire.otherDetails"
                    placeholder="Please specify"
                    class="w-full"
                  />
                </div>
              </div>
            </div>
            <div class="field">
              <label class="block mb-2">Additional Comments (Optional)</label>
              <Textarea v-model="questionnaire.additionalComments" rows="3" class="w-full" />
            </div>
          </div>

          <div class="flex justify-content-end gap-2 mt-auto pt-3 border-top-1 surface-border">
            <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="handleCancel" />
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="saving"
              :disabled="!canSave"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Front Image Cropper Dialog -->
    <Dialog
      :visible="frontCapture.state.showCropperDialog"
      header="Crop Front ID Image"
      :modal="true"
      :closable="false"
      :style="{ width: '90vw', maxWidth: '600px' }"
      :breakpoints="{ '960px': '90vw' }"
    >
      <div class="cropper-container">
        <Cropper
          v-if="frontCapture.state.cropperImage"
          class="cropper"
          :src="frontCapture.state.cropperImage"
          :stencil-props="{
            aspectRatio: 5 / 3,
          }"
          image-restriction="stencil"
          :canvas="{
            width: 600,
            height: 400,
          }"
          ref="frontCropperRef"
        />

        <div class="flex justify-content-center mt-3">
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text mr-2"
            @click="frontCapture.rotateImage(90)"
          />
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text mr-2"
            @click="frontCapture.rotateImage(-90)"
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text mr-2"
            @click="frontCapture.cancelCrop"
          />
          <Button label="Crop" icon="pi pi-check" @click="frontCapture.cropImage" />
        </div>
      </div>
    </Dialog>

    <!-- Back Image Cropper Dialog -->
    <Dialog
      :visible="backCapture.state.showCropperDialog"
      header="Crop Back ID Image"
      :modal="true"
      :closable="false"
      :style="{ width: '90vw', maxWidth: '600px' }"
      :breakpoints="{ '960px': '90vw' }"
    >
      <div class="cropper-container">
        <Cropper
          v-if="backCapture.state.cropperImage"
          class="cropper"
          :src="backCapture.state.cropperImage"
          :stencil-props="{
            aspectRatio: 5 / 3,
          }"
          image-restriction="stencil"
          :canvas="{
            width: 600,
            height: 400,
          }"
          ref="backCropperRef"
        />

        <div class="flex justify-content-center mt-3">
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text mr-2"
            @click="backCapture.rotateImage(90)"
          />
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text mr-2"
            @click="backCapture.rotateImage(-90)"
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text mr-2"
            @click="backCapture.cancelCrop"
          />
          <Button label="Crop" icon="pi pi-check" @click="backCapture.cropImage" />
        </div>
      </div>
    </Dialog>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputMask from 'primevue/inputmask'
import RadioButton from 'primevue/radiobutton'
import Textarea from 'primevue/textarea'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useIdCapture } from '@/composables/useIdCapture'
import { usePersonStore } from '@/stores/v2/person'
import { useIDDocumentStore } from '@/stores/idDocumentStore'
import { firestoreService } from '@/utils/firestoreService'
import { compressImage, uploadImage } from '@/utils/imageService'
import type { Person } from '@/types/Person'
import type { IDDocument } from '@/types/IDDocument'

interface Props {
  visible: boolean
  scannedData?: {
    firstName?: string
    middleName?: string
    lastName?: string
    idNumber?: string
    state?: string
    dateOfBirth?: string
    address?: string
    city?: string
    postalCode?: string
    country?: string
    barcodeHash?: string
    rawData?: string
  }
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'person:new', person: Person): void
  (e: 'document', document: IDDocument): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Stores
const personStore = usePersonStore()
const idDocumentStore = useIDDocumentStore()

// Refs
const frontInputRef = ref<HTMLInputElement>()
const backInputRef = ref<HTMLInputElement>()
const frontCropperRef = ref()
const backCropperRef = ref()

// State
const saving = ref(false)
const frontImageUrl = ref<string>()
const backImageUrl = ref<string>()

// Contact form
const contactForm = ref({
  phone: '',
  email: '',
})

// Questionnaire
const questionnaire = ref({
  referralSource: '',
  socialMediaDetails: '',
  otherDetails: '',
  additionalComments: '',
})

// ID Capture composables
const frontCapture = useIdCapture(frontCropperRef)
const backCapture = useIdCapture(backCropperRef)

// Computed
const canSave = computed(() => {
  return contactForm.value.phone || contactForm.value.email
})

// Methods
const triggerFrontSelect = () => {
  frontInputRef.value?.click()
}

const triggerBackSelect = () => {
  backInputRef.value?.click()
}

const onFrontFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    frontCapture.handleFileSelect(file)
  }
}

const onBackFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    backCapture.handleFileSelect(file)
  }
}

const handleCancel = () => {
  emit('update:visible', false)
}

const handleSave = async () => {
  if (!canSave.value) return

  saving.value = true
  try {
    // Create person data (without photoIdUrl initially)
    const personData = {
      firstName: props.scannedData?.firstName || '',
      middleName: props.scannedData?.middleName || '',
      lastName: props.scannedData?.lastName || '',
      phone: contactForm.value.phone,
      email: contactForm.value.email,
      idNumber: props.scannedData?.idNumber || '',
      streetAddress: props.scannedData?.address || '',
      city: props.scannedData?.city || '',
      state: props.scannedData?.state || '',
      postalCode: props.scannedData?.postalCode || '',
      country: props.scannedData?.country || '',
      barcodeHash: props.scannedData?.barcodeHash || '',
      status: 'supplier' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    // Create person first
    const personId = await personStore.create(personData)
    const person = (await personStore.get(personId)) as Person | null
    if (!person) {
      throw new Error('Failed to create person')
    }
    console.log('[NewSupplierDialog] Created person:', person)

    // Upload images and get URLs
    let frontImageId: string | undefined
    let backImageId: string | undefined

    if (frontCapture.state.uploadedImagePreview) {
      // Convert data URL to File
      const response = await fetch(frontCapture.state.uploadedImagePreview)
      const blob = await response.blob()
      const file = new File([blob], 'front-id.jpg', { type: 'image/jpeg' })
      const compressedImage = await compressImage(file)
      frontImageId = await uploadImage(compressedImage, `id-documents/${person.id}/front`)
    }

    if (backCapture.state.uploadedImagePreview) {
      // Convert data URL to File
      const response = await fetch(backCapture.state.uploadedImagePreview)
      const blob = await response.blob()
      const file = new File([blob], 'back-id.jpg', { type: 'image/jpeg' })
      const compressedImage = await compressImage(file)
      backImageId = await uploadImage(compressedImage, `id-documents/${person.id}/back`)
    }

    // Update person with photoIdUrl if front image was uploaded
    if (frontImageId) {
      await personStore.update(person.id!, { photoIdUrl: frontImageId })
    }

    // Create ID document
    const idDocumentData = {
      personId: person.id,
      type: 'driver_license' as const,
      idNumber: props.scannedData?.idNumber || '',
      issuer: props.scannedData?.state || '',
      frontImageUrl: frontImageId,
      backImageUrl: backImageId,
      barcodeHash: props.scannedData?.barcodeHash || '',
      rawBarcodeData: props.scannedData?.rawData || '',
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    const idDocument = await idDocumentStore.add(idDocumentData)
    console.log('[NewSupplierDialog] Created ID document:', idDocument)

    // Emit events
    emit('person:new', person)
    emit('document', idDocument)
    emit('update:visible', false)

    // Reset form
    contactForm.value = { phone: '', email: '' }
    questionnaire.value = {
      referralSource: '',
      socialMediaDetails: '',
      otherDetails: '',
      additionalComments: '',
    }
    frontImageUrl.value = undefined
    backImageUrl.value = undefined
    frontCapture.reset()
    backCapture.reset()
  } catch (error) {
    console.error('[NewSupplierDialog] Error creating person and document:', error)
  } finally {
    saving.value = false
  }
}

// Watch for cropper state changes
watch(
  () => frontCapture.state.uploadedImagePreview,
  (newPreview) => {
    if (newPreview) {
      frontImageUrl.value = newPreview
    }
  },
)

watch(
  () => backCapture.state.uploadedImagePreview,
  (newPreview) => {
    if (newPreview) {
      backImageUrl.value = newPreview
    }
  },
)
</script>

<style scoped>
.cropper-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cropper {
  width: 100%;
  max-width: 500px;
  height: 300px;
}

.id-photo-container-half {
  width: 100%;
  min-height: 120px;
}

.id-photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
